import { ProtocolCriteria } from '../types/protocol'
import './ProtocolCriteriaEditor.css'

const CRITERIA_FIELDS: { value: string; label: string }[] = [
  { value: 'age', label: 'Age' },
  { value: 'gender', label: 'Gender' },
  { value: 'condition', label: 'Condition' },
  { value: 'medication', label: 'Medication' },
]

const OPERATORS: { value: ProtocolCriteria['operator']; label: string }[] = [
  { value: 'equals', label: 'equals' },
  { value: 'notEquals', label: 'does not equal' },
  { value: 'greaterThan', label: 'greater than' },
  { value: 'lessThan', label: 'less than' },
  { value: 'contains', label: 'contains' },
  { value: 'in', label: 'in (comma-separated)' },
  { value: 'notIn', label: 'not in (comma-separated)' },
]

interface ProtocolCriteriaEditorProps {
  criteria: ProtocolCriteria[]
  onChange: (criteria: ProtocolCriteria[]) => void
}

function newCriterion(): ProtocolCriteria {
  return {
    field: 'age',
    operator: 'greaterThan',
    value: 18,
  }
}

function parseValue(field: string, operator: string, raw: string): any {
  if (operator === 'in' || operator === 'notIn') {
    return raw
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  }
  if (field === 'age') {
    const n = parseInt(raw, 10)
    return isNaN(n) ? 0 : n
  }
  return raw
}

export function ProtocolCriteriaEditor({ criteria, onChange }: ProtocolCriteriaEditorProps) {
  const addCriterion = () => {
    onChange([...criteria, newCriterion()])
  }

  const updateCriterion = (index: number, updates: Partial<ProtocolCriteria>) => {
    const newCriteria = [...criteria]
    newCriteria[index] = { ...newCriteria[index], ...updates }
    onChange(newCriteria)
  }

  const removeCriterion = (index: number) => {
    onChange(criteria.filter((_, i) => i !== index))
  }

  const getValueDisplay = (c: ProtocolCriteria): string => {
    if (Array.isArray(c.value)) {
      return c.value.join(', ')
    }
    return String(c.value ?? '')
  }

  const setValueFromInput = (index: number, raw: string) => {
    const c = criteria[index]
    const parsed = parseValue(c.field, c.operator, raw)
    updateCriterion(index, { value: parsed })
  }

  return (
    <div className="protocol-criteria-editor">
      <div className="criteria-editor-header">
        <h3>Eligibility Criteria</h3>
        <p className="criteria-editor-help">
          Patients must meet all criteria to be eligible. Leave empty to allow all patients.
        </p>
        <button type="button" className="btn-add-criterion" onClick={addCriterion}>
          + Add Criterion
        </button>
      </div>

      {criteria.length === 0 ? (
        <div className="criteria-empty">
          <p>No criteria. All patients will be eligible. Add criteria to restrict eligibility (e.g. age ≥ 18, has condition).</p>
        </div>
      ) : (
        <div className="criteria-list">
          {criteria.map((criterion, index) => (
            <div key={index} className="criterion-row">
              <select
                value={criterion.field}
                onChange={(e) =>
                  updateCriterion(index, {
                    field: e.target.value,
                    value: e.target.value === 'age' ? 18 : '',
                  })
                }
                aria-label="Criteria field"
              >
                {CRITERIA_FIELDS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <select
                value={criterion.operator}
                onChange={(e) =>
                  updateCriterion(index, {
                    operator: e.target.value as ProtocolCriteria['operator'],
                  })
                }
                aria-label="Operator"
              >
                {OPERATORS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              <input
                type={criterion.field === 'age' && (criterion.operator === 'equals' || criterion.operator === 'notEquals' || criterion.operator === 'greaterThan' || criterion.operator === 'lessThan') ? 'number' : 'text'}
                value={getValueDisplay(criterion)}
                onChange={(e) => setValueFromInput(index, e.target.value)}
                placeholder={
                  criterion.field === 'age'
                    ? 'e.g. 18'
                    : criterion.field === 'gender'
                      ? 'e.g. M, F'
                      : criterion.operator === 'in' || criterion.operator === 'notIn'
                        ? 'e.g. value1, value2'
                        : 'e.g. diabetes'
                }
                className="criterion-value-input"
                aria-label="Value"
              />
              <button
                type="button"
                className="btn-remove-criterion"
                onClick={() => removeCriterion(index)}
                aria-label="Remove criterion"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
